import {clamp} from '@benchling/js-utils';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {RefObject} from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';

import * as benchlingUtil from 'BenchlingUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {verticallyScrollItemIntoView} from 'util/Scroll';
import {ElementKeyboardEvent} from 'util/Types';

import {AttachDropdownItem, OptionHeight} from './AttachDropdownConstants';
import AttachDropdownNoResultsMenu from './AttachDropdownNoResultsMenu';
import AttachDropdownOptionsMenu, {
  AttachDropdownOptionContainer,
  RenderAttachDropdownOptionInfo,
  SelectAttachment,
} from './AttachDropdownOptionsMenu';
import getDefaultRenderAttachDropdownOptionInfo from './getDefaultRenderAttachDropdownOptionInfo';

/**
 * There are two separate arrays that the AttachDropdown component manages. The first is the
 * normal `results` array, and the second is the `noResults` array. The following object helps
 * manage the two different array indices.
 */
const NoResultsIndexManager = {
  // Not for you.
  _baseIndex: 1e5,

  // Given a raw index into the `no results` array, we adjust it so that it can be interpreted
  // as such (e.g., by `isIndexInNoResultsGroup()`)
  createNoResultsIndex(rawIndex: number): number {
    return rawIndex + NoResultsIndexManager._baseIndex;
  },
  // If you only have an index, we'll tell you if it's in the "no results" group
  isIndexInNoResultsGroup(index: number): boolean {
    return index >= NoResultsIndexManager._baseIndex;
  },
  // If you have a "noResults" index, this converts it back into the raw index in the array
  convertNoResultsIndexToRaw(index: number): number {
    return index - NoResultsIndexManager._baseIndex;
  },
};

interface AttachDropdownOuterWrapperProps {
  dropup: boolean;
  style?: object;
  width?: number | string;
  children: JSX.Element;
  // Indicates if the children of the wrappper will be `<li>`s and thus requires a `<ul>` wrapper.
  forMenu: boolean;
}

export const AttachDropdownOuterWrapper = React.forwardRef<
  HTMLDivElement | HTMLUListElement,
  AttachDropdownOuterWrapperProps
>((props, ref) => {
  const {dropup, style, width, children} = props;
  const menuProps = {
    ref,
    className: 'attachDropdown-menu dropdown-menu',
    style: {
      width,
      maxHeight: props.forMenu ? AttachDropdown.MAX_HEIGHT : undefined,
    },
  };
  return (
    <div
      className={classNames('attachDropdown dropdown open', {dropup})}
      style={style}
      {...getTestProps({component: 'AttachDropdown', element: 'root'})}
      role="listbox"
    >
      {props.forMenu ? (
        <ul {...menuProps} ref={ref as React.Ref<HTMLUListElement>}>
          {children}
        </ul>
      ) : (
        <div {...menuProps} ref={ref as React.Ref<HTMLDivElement>}>
          {children}
        </div>
      )}
    </div>
  );
});

export interface NoResultsItem {
  menuItem: JSX.Element;
  onClick: () => void;
}
export interface AttachDropdownProps {
  dropup: boolean;
  resultsRequireQuery: boolean;
  emptyQueryMessage: JSX.Element | string;
  noResultsMessage: string;
  errorMessage?: string | null;
  query: string;
  results: Array<AttachDropdownItem>;
  // If you provide any "no results" items, they will be rendered when no search has been initiated.
  // Typically your menuItem should be a CustomAttachDropdownOption element, which will be shown
  // underneath the emptyQueryMessage item.
  noResultsItems?: Array<NoResultsItem> | null;
  selectAttachment: SelectAttachment;
  searching: boolean;
  width?: number | string;
  close?: () => void;
  defaultOptionSelected?: boolean;
  style?: object;
  getRenderOptionInfo: (option: AttachDropdownItem) => RenderAttachDropdownOptionInfo;
  height: OptionHeight;
}

interface State {
  hoverIndex: number;
}
const initialState = {
  hoverIndex: -1,
};

class AttachDropdown extends React.Component<AttachDropdownProps, State> {
  static propTypes = {
    dropup: PropTypes.bool.isRequired,
    resultsRequireQuery: PropTypes.bool.isRequired,
    emptyQueryMessage: PropTypes.node.isRequired,
    noResultsMessage: PropTypes.string.isRequired,
    errorMessage: PropTypes.string,
    query: PropTypes.string.isRequired,
    results: PropTypes.array.isRequired,
    noResultsItems: PropTypes.arrayOf(
      PropTypes.shape({
        menuItem: PropTypes.node.isRequired,
        onClick: PropTypes.func.isRequired,
      })
    ),
    selectAttachment: PropTypes.func.isRequired,
    searching: PropTypes.bool.isRequired,
    width: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
    close: PropTypes.func,
    defaultOptionSelected: PropTypes.bool,
    style: PropTypes.object,
    getRenderOptionInfo: PropTypes.func.isRequired,
    height: PropTypes.oneOf(Object.values(OptionHeight)).isRequired,
  };
  static defaultProps = {
    dropup: false,
    resultsRequireQuery: true,
    emptyQueryMessage: 'Search for an item.',
    noResultsMessage: 'No results found.',
    getRenderOptionInfo: getDefaultRenderAttachDropdownOptionInfo,
    height: OptionHeight.LARGE,
  };

  override state: State = initialState;

  static MAX_HEIGHT = 200;
  static HEIGHTS = OptionHeight;

  optionRefs: Array<RefObject<AttachDropdownOptionContainer>> = [];
  noResultsOptionRefs: Array<RefObject<AttachDropdownOptionContainer>> = [];
  scrollAreaRef = React.createRef<HTMLUListElement>();

  override componentDidMount(): void {
    this.optionRefs = this.props.results.map(() => React.createRef<AttachDropdownOptionContainer>());
    this.noResultsOptionRefs = (this.props.noResultsItems ?? []).map(() =>
      React.createRef<AttachDropdownOptionContainer>()
    );
    this._setHoverStateFromProps(this.props);
  }

  override UNSAFE_componentWillReceiveProps(nextProps: AttachDropdownProps): void {
    if (!isEqual(this.props.results, nextProps.results)) {
      this._setHoverStateFromProps(nextProps);
      this.optionRefs = nextProps.results.map(() => React.createRef<AttachDropdownOptionContainer>());
    }
    // Ensure we have at least enough refs for the noResults
    const newNumberOfNoResultsItems = nextProps.noResultsItems?.length ?? 0;
    const oldNumberOfNoResultsItems = this.props.noResultsItems?.length ?? 0;
    for (let i = oldNumberOfNoResultsItems; i < newNumberOfNoResultsItems; ++i) {
      this.noResultsOptionRefs.push(React.createRef<AttachDropdownOptionContainer>());
    }
  }

  setHoverIndex = (index: number): void => {
    this.setState({hoverIndex: index});
  };

  _setHoverStateFromProps(props: AttachDropdownProps): void {
    this.setState({
      hoverIndex: props.results.length > 0 && props.defaultOptionSelected ? 0 : -1,
    });
  }

  getItemAt(index: number): {ref: AttachDropdownOptionContainer | null; onClick: (() => void) | null} | null {
    if (index < 0) {
      return null;
    } else if (NoResultsIndexManager.isIndexInNoResultsGroup(index)) {
      return {
        ref: this.noResultsOptionRefs[NoResultsIndexManager.convertNoResultsIndexToRaw(index)].current,
        onClick: this.props.noResultsItems![NoResultsIndexManager.convertNoResultsIndexToRaw(index)].onClick,
      };
    } else {
      const result = this.props.results[index];
      const isClickable = this.props.getRenderOptionInfo(result).clickable;
      return {
        ref: this.optionRefs[index].current,
        onClick: isClickable
          ? () => {
              // TODO(T6527): Don't duplicate the call to selectAttachment with AttachDropdownOptionsMenu
              this.props.selectAttachment(
                result.item_id,
                result.name,
                result.item.editURL ? benchlingUtil.pathToURL(result.item.editURL) : '',
                result.item,
                result.internalLink,
                index
              );
            }
          : null,
      };
    }
  }

  queryKeydownHandler(e: ElementKeyboardEvent): boolean {
    switch (e.which) {
      case 38: {
        // Up
        if (this.props.results.length === 0) {
          return false;
        }
        const newIndex = clamp(0, this.props.results.length - 1, this.state.hoverIndex - 1);
        this.scrollIndexIntoView(newIndex);
        this.setState({hoverIndex: newIndex});
        break;
      }
      case 40: {
        // Down
        if (this.props.results.length === 0) {
          return false;
        }
        const newIndex = clamp(0, this.props.results.length - 1, this.state.hoverIndex + 1);
        this.scrollIndexIntoView(newIndex);
        this.setState({hoverIndex: newIndex});
        break;
      }
      case 13: {
        // Enter
        const item = this.getItemAt(this.state.hoverIndex);
        if (!item || !item.onClick) {
          return false;
        }
        item.onClick();
        break;
      }
      case 27: {
        // Esc
        if (this.props.close == null) {
          return false;
        }
        this.props.close();
        break;
      }
      default:
        return false;
    }

    // If the component handled the event, prevent the default behavior and signal the parent.
    e.preventDefault();
    return true;
  }

  scrollIndexIntoView(index: number): void {
    // Ignore cases where we clamped the index and it hasn't changed
    if (index === this.state.hoverIndex) {
      return;
    }
    // ...and when no option is highlighted
    const itemRef = this.getItemAt(index)?.ref;
    if (!itemRef) {
      return;
    }
    const scrollArea = ReactDOM.findDOMNode(this.scrollAreaRef.current) as HTMLUListElement;
    const item = ReactDOM.findDOMNode(itemRef) as Element;
    verticallyScrollItemIntoView(scrollArea, item);
  }

  _noResultsHeader(): string | JSX.Element {
    if (this.props.errorMessage) {
      return this.props.errorMessage;
    } else if (this.props.searching) {
      return <CenteredHorizontalThrobber suppressPadding={true} />;
    } else if (this.props.query !== '' || !this.props.resultsRequireQuery) {
      return this.props.noResultsMessage;
    } else {
      return this.props.emptyQueryMessage;
    }
  }

  _shouldRenderNoResultsOptions(): boolean {
    return (
      this.props.noResultsItems != null &&
      this.props.noResultsItems.length > 0 &&
      !this.props.searching &&
      this.props.results.length === 0
    );
  }

  override render(): JSX.Element {
    return (
      <AttachDropdownOuterWrapper
        dropup={this.props.dropup}
        style={this.props.style}
        width={this.props.width}
        ref={this.scrollAreaRef}
        forMenu={true}
      >
        {this.props.results.length ? (
          <AttachDropdownOptionsMenu
            results={this.props.results}
            selectAttachment={this.props.selectAttachment}
            hoverIndex={this.state.hoverIndex}
            setHoverIndex={this.setHoverIndex}
            refs={this.optionRefs}
            getRenderOptionInfo={this.props.getRenderOptionInfo}
            height={this.props.height}
          />
        ) : (
          <AttachDropdownNoResultsMenu
            headerContents={this._noResultsHeader()}
            close={this.props.close && !this.props.searching ? this.props.close : null}
            hoverIndex={this.state.hoverIndex}
            height={this.props.height}
            noResultsItems={
              this._shouldRenderNoResultsOptions()
                ? this.props.noResultsItems!.map(({menuItem, onClick}, index) => ({
                    ref: this.noResultsOptionRefs[index],
                    index: NoResultsIndexManager.createNoResultsIndex(index),
                    onHover: () => {
                      this.setHoverIndex(NoResultsIndexManager.createNoResultsIndex(index));
                    },
                    onClick,
                    menuItem,
                  }))
                : []
            }
          />
        )}
      </AttachDropdownOuterWrapper>
    );
  }
}

export default AttachDropdown;
